// @ts-nocheck
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Settings, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const PERIOD_TYPES = [
  { value: 'none', label: 'No periods (single final score)' },
  { value: 'quarters', label: 'Quarters' },
  { value: 'halves', label: 'Halves' },
  { value: 'innings', label: 'Innings' },
  { value: 'periods', label: 'Periods' },
];

const DEFAULT_COUNT: Record<string, number> = {
  quarters: 4,
  halves: 2,
  innings: 9,
  periods: 3,
};

interface Props {
  eventId: string;
  periodType: string;
  periodCount: number;
  onSaved: (cfg: { period_type: string; period_count: number }) => void;
}

const PeriodSettings: React.FC<Props> = ({ eventId, periodType, periodCount, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState(periodType || 'none');
  const [count, setCount] = useState<string>(String(periodCount || DEFAULT_COUNT[periodType] || 4));
  const [saving, setSaving] = useState(false);

  const handleTypeChange = (v: string) => {
    setType(v);
    if (v !== 'none') setCount(String(DEFAULT_COUNT[v] || 4));
  };

  const save = async () => {
    setSaving(true);
    const numCount = type === 'none' ? 0 : Math.max(1, Math.min(parseInt(count, 10) || 1, 20));
    try {
      const { error } = await supabase
        .from('event_format_config')
        .upsert(
          { event_id: eventId, period_type: type, period_count: numCount },
          { onConflict: 'event_id' },
        );
      if (error) throw error;
      onSaved({ period_type: type, period_count: numCount });
      toast.success('Scoring format saved.');
      setOpen(false);
    } catch (err: any) {
      console.error('save period config error', err);
      toast.error(err?.message || 'Could not save scoring format.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <Settings className="mr-2 h-4 w-4" />
          Scoring format
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72">
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">
              Period type
            </label>
            <Select value={type} onValueChange={handleTypeChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_TYPES.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {type !== 'none' && (
            <div>
              <label className="mb-1 block text-xs font-semibold text-muted-foreground">
                Number of periods
              </label>
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={2}
                value={count}
                onChange={(e) => setCount(e.target.value.replace(/[^0-9]/g, '').slice(0, 2))}
              />
            </div>
          )}
          <Button className="w-full" disabled={saving} onClick={save}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default PeriodSettings;
